import React, { useState, useEffect } from 'react';
import { Loader2, TrendingUp } from 'lucide-react';
import { getUserWeeklyTrends } from '@/utils/api';
import { toast } from 'sonner';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

/**
 * WeeklyTrend - Weekly nutritional trend charts
 * Extracted from AdminDashboard.js for better code organization
 */
const WeeklyTrend = ({ userId, token }) => {
  const [trendsData, setTrendsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedWeek, setSelectedWeek] = useState(0);
  const [selectedMacro, setSelectedMacro] = useState('protein');
  const [numWeeks, setNumWeeks] = useState(8);

  useEffect(() => {
    fetchTrends();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [numWeeks, userId, token]);

  const fetchTrends = async () => {
    setLoading(true);
    try {
      const data = await getUserWeeklyTrends(userId, numWeeks, token);
      setTrendsData(data);
    } catch (error) {
      console.error('Error fetching trends:', error);
      toast.error('Failed to load weekly trends');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!trendsData || !trendsData.weeks_data) {
    return <div className="text-center py-8 text-muted-foreground">No trend data available</div>;
  }

  // Prepare data for bar chart (selected week vs targets)
  const selectedWeekData = trendsData.weeks_data[selectedWeek];
  const targets = trendsData.targets || { calories: 2000, protein: 80, carbs: 250, fat: 65, vitamins: 1286, minerals: 4628 };
  
  const barChartData = [
    { name: 'Calories', actual: selectedWeekData?.avg_calories || 0, target: targets.calories },
    { name: 'Protein', actual: selectedWeekData?.avg_protein || 0, target: targets.protein },
    { name: 'Carbs', actual: selectedWeekData?.avg_carbs || 0, target: targets.carbs },
    { name: 'Fat', actual: selectedWeekData?.avg_fat || 0, target: targets.fat },
  ];

  // Prepare data for line chart (week over week macro trends)
  const macroKey = selectedMacro === 'protein' ? 'avg_protein' : selectedMacro === 'carbs' ? 'avg_carbs' : 'avg_fat';
  const targetKey = selectedMacro === 'protein' ? 'protein' : selectedMacro === 'carbs' ? 'carbs' : 'fat';
  
  const lineChartData = trendsData.weeks_data.map(week => ({
    week: week.week_label,
    actual: week[macroKey],
    target: targets[targetKey]
  }));

  return (
    <div className="space-y-8">
      {/* Week selector */}
      <div className="flex flex-wrap items-center gap-4">
        <div className="flex items-center gap-3">
          <label className="text-sm font-medium">Number of weeks:</label>
          <select
            value={numWeeks}
            onChange={(e) => setNumWeeks(Number(e.target.value))}
            className="bg-muted border border-border rounded-lg px-3 py-2 text-sm"
            data-testid="num-weeks-selector"
          >
            <option value={4}>4 weeks</option>
            <option value={8}>8 weeks</option>
            <option value={12}>12 weeks</option>
            <option value={26}>26 weeks</option>
          </select>
        </div>
        <div className="flex items-center gap-3">
          <label className="text-sm font-medium">Compare week:</label>
          <select
            value={selectedWeek}
            onChange={(e) => setSelectedWeek(Number(e.target.value))}
            className="bg-muted border border-border rounded-lg px-3 py-2 text-sm"
            data-testid="week-selector"
          >
            {trendsData.weeks_data.map((week, idx) => (
              <option key={`week-${week.week_label}-${week.week_end}`} value={idx}>
                {week.week_label} - {week.week_end}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Bar Chart - Weekly Average vs Targets */}
      <div className="bg-card rounded-2xl border border-border/50 p-6">
        <h4 className="font-bold text-lg mb-4 flex items-center gap-2">
          <TrendingUp className="w-5 h-5 text-primary" />
          Weekly Average vs Targets ({selectedWeekData?.week_label})
        </h4>
        <p className="text-sm text-muted-foreground mb-4">
          {selectedWeekData?.days_with_data || 0} days with data, {selectedWeekData?.total_meals || 0} meals logged
        </p>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={barChartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="name" />
            <YAxis />
            <Tooltip />
            <Legend />
            <Bar dataKey="actual" fill="#6B8E23" name="Actual Average" />
            <Bar dataKey="target" fill="#FF8042" name="Target" />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Macro selector for line chart */}
      <div className="flex items-center gap-4">
        <label className="text-sm font-medium">Track macro over time:</label>
        <div className="flex gap-2">
          {['protein', 'carbs', 'fat'].map(macro => (
            <button
              key={macro}
              onClick={() => setSelectedMacro(macro)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                selectedMacro === macro
                  ? 'bg-primary text-white'
                  : 'bg-muted text-muted-foreground hover:bg-muted/80'
              }`}
              data-testid={`macro-btn-${macro}`}
            >
              {macro.charAt(0).toUpperCase() + macro.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* Line Chart - Week over week macro trends */}
      <div className="bg-card rounded-2xl border border-border/50 p-6">
        <h4 className="font-bold text-lg mb-4">
          {selectedMacro.charAt(0).toUpperCase() + selectedMacro.slice(1)} Intake Over Time (Weekly Avg)
        </h4>
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={lineChartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="week" />
            <YAxis />
            <Tooltip />
            <Legend />
            <Line type="monotone" dataKey="actual" stroke="#6B8E23" strokeWidth={2} name={`Avg ${selectedMacro} (g)`} dot={{ r: 4 }} />
            <Line type="monotone" dataKey="target" stroke="#FF8042" strokeDasharray="5 5" name="Target" dot={false} />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Weekly breakdown table */}
      <div className="bg-card rounded-2xl border border-border/50 overflow-hidden">
        <div className="p-4 border-b border-border bg-muted/50">
          <h4 className="font-bold">Weekly Breakdown</h4>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-muted/30">
              <tr>
                <th className="text-left px-4 py-3 text-xs font-bold uppercase">Week</th>
                <th className="text-right px-4 py-3 text-xs font-bold uppercase">Days</th>
                <th className="text-right px-4 py-3 text-xs font-bold uppercase">Meals</th>
                <th className="text-right px-4 py-3 text-xs font-bold uppercase">Avg Cal</th>
                <th className="text-right px-4 py-3 text-xs font-bold uppercase">Avg Protein</th>
                <th className="text-right px-4 py-3 text-xs font-bold uppercase">Avg Carbs</th>
                <th className="text-right px-4 py-3 text-xs font-bold uppercase">Avg Fat</th>
              </tr>
            </thead>
            <tbody>
              {trendsData.weeks_data.map((week, idx) => (
                <tr 
                  key={`trend-row-${week.week_label}`} 
                  className={`border-b border-border/50 ${idx === selectedWeek ? 'bg-primary/10' : 'hover:bg-muted/20'}`}
                >
                  <td className="px-4 py-3 text-sm font-medium">{week.week_label}</td>
                  <td className="px-4 py-3 text-sm text-right">{week.days_with_data}</td>
                  <td className="px-4 py-3 text-sm text-right">{week.total_meals}</td>
                  <td className="px-4 py-3 text-sm text-right">{week.avg_calories.toFixed(0)}</td>
                  <td className="px-4 py-3 text-sm text-right">{week.avg_protein.toFixed(1)}g</td>
                  <td className="px-4 py-3 text-sm text-right">{week.avg_carbs.toFixed(1)}g</td>
                  <td className="px-4 py-3 text-sm text-right">{week.avg_fat.toFixed(1)}g</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default WeeklyTrend;
